<template>
  <v-row justify="center">
    <v-col cols="10" class="mt-4">
      <v-card>
        <v-card-title class="primary text-white">
          <div>
            <h3 class="text-h5">
              <span>{{ $route.params.id ? 'Update' : 'Add' }} Product</span>
            </h3>
          </div>
        </v-card-title>

        <v-progress-linear
          :active="productPending"
          :indeterminate="true"
          class="ma-0"
          height="4"
          style="top: -2px;"
        >
          Loading...
        </v-progress-linear>

        <v-form ref="form" @submit.prevent="submitHandler" lazy-validation v-if="!productPending">
          <product-add-edit-form
            ref="productAddEditForm"
            :initialProduct="product"
            :productTypeDisabled="productTypeDisabled"
          />

          <v-card-actions class="d-flex justify-end">
            <v-btn
              class="flex-grow-0"
              @click="endAction()"
              :disabled="productPending"
            >
              Cancel
            </v-btn>
            <v-btn
              v-if="$route.params.id && hasAccess"
              @click="deleteDialogOpened = true"
              color="error"
              class="flex-grow-0"
              :disabled="productPending"
            >
              Delete
            </v-btn>
            <v-btn
              v-if="hasAccess"
              type="submit"
              color="primary"
              class="flex-grow-0"
              :disabled="productPending"
            >
              {{$route.params.id ? 'Update' : 'Create'}}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>

      <template>
        <div class="d-flex justify-center">
          <v-dialog
            v-model="deleteDialogOpened"
            max-width="800"
          >
            <v-card>
              <v-card-title
                class="text-h5 error text-white"
                >
                Delete Product
              </v-card-title>

              <v-card-text>
                <div class="text-h6" style="line-height:1.4 !important">
                  <p class="mt-2 mb-3">
                    Do you really want to delete <strong>"{{product.name || ''}}"?</strong>
                  </p>
                </div>
              </v-card-text>
              <v-divider></v-divider>

              <v-card-actions class="py-3 px-3">
                <v-spacer></v-spacer>
                <v-btn
                  variant="outlined"
                  color="error"
                  @click="deleteDialogOpened = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  :loading="deleteDialogPending"
                  :disabled="deleteDialogPending"
                  color="error"
                  @click="deleteProduct()"
                >
                  Delete
                  <template v-slot:loader>
                    <span>Saving...</span>
                  </template>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </template>
    </v-col>
  </v-row>
</template>

<script>
  import Product from '@/models/Product';
  import ProductAddEditForm from './ProductAddEditForm';
  import axios from '@/axios';

  export default {
    components: {
      'product-add-edit-form': ProductAddEditForm,
    },

    data () {
      return {
        product: Product.generateEmpty(),

        productTypeDisabled: false,
        productPending: true,

        deleteDialogOpened: false,
        deleteDialogPending: false,
      }
    },

    computed: {
      hasAccess() {
        let allowedRoles = [];

        if (this.$route.params.id) {
          allowedRoles = ['Super Admin'];
        } else {
          // Route-fork PR-3 rename: "Distributor Manager" -> "Owner".
          allowedRoles = ['Super Admin', 'Owner'];
        }

        const currentRole = this.$store.state.account.role.name;

        return !!(currentRole && ~allowedRoles.indexOf(currentRole));
      },

      endAction() {
        return () => this.$router.push(`/distributor/products`);
      }
    },

    mounted () {
      if (this.$route.params.id) {
        this.loadProduct();
      } else {
        this.productPending = false;
      }
    },

    methods: {
      async loadProduct() {
        this.productPending = true;

        try {
          let product = await this.$store.dispatch(
            'jv/get',
            [
              `/products/${this.$route.params.id}`,
              {params: {include: 'distributor,product_type,campaign_products,supplier'}}
            ]
          );

          this.product = product;

          if (Object.values(product.campaign_products).length > 0) {
            this.productTypeDisabled = true;
          }
        } finally {
          this.productPending = false;
        }
      },

      async submitHandler() {
        const { valid } = await this.$refs.form.validate();
        if (valid) {
          // get product from the form component
          this.product = this.$refs.productAddEditForm.getProduct();

          // either create or update the model
          if (this.$route.params.id) {
            // Use axios directly — jv/patch mishandles relationship objects
            // and reactive proxies, causing 405s on the collection endpoint.
            const productId = this.$route.params.id;
            const payload = {
              data: {
                type: 'Product',
                id: productId,
                attributes: {
                  name: this.product.name,
                  item_number: this.product.item_number,
                  product_tax_code: this.product.product_tax_code,
                  distributor_id: this.product.distributor_id,
                  product_type_id: this.product.product_type_id,
                  supplier_id: this.product.supplier_id,
                }
              }
            };

            await axios.patch(`/products/${productId}/`, payload);

            this.$store.dispatch('raiseSuccess', `Product ${productId} updated`);
            this.endAction();
          } else {
            // For POST, use axios directly with proper JSON:API relationship format
            // Extract product_type_id from the relationship object
            const product_type_id = this.product.product_type && this.product.product_type._jv 
              ? this.product.product_type._jv.id 
              : this.product.product_type_id;
            
            // Build JSON:API compliant payload with relationship in proper format
            const payload = {
              data: {
                type: 'Product',
                attributes: {
                  name: this.product.name,
                  item_number: this.product.item_number
                },
                relationships: {
                  product_type: {
                    data: {
                      type: 'ProductType',
                      id: product_type_id
                    }
                  },
                  ...(this.product.supplier ? {
                    supplier: {
                      data: {
                        type: 'Vendor',
                        id: this.product.supplier._jv ? this.product.supplier._jv.id : this.product.supplier.id
                      }
                    }
                  } : {})
                }
              }
            };
            
            // Add optional attributes
            if (this.product.description) payload.data.attributes.description = this.product.description;
            if (this.product.cost !== undefined) payload.data.attributes.cost = this.product.cost;
            if (this.product.price !== undefined) payload.data.attributes.price = this.product.price;
            
            // Use axios directly instead of jv/post
            const response = await axios.post('/products?include=product_type', payload);
            
            // Normalize the response into jsonapi-vuex format and update store
            const product = await this.$store.dispatch('jv/get', {
              _jv: {
                type: 'Product',
                id: response.data.data.id
              }
            });

            this.product = product;
            this.$store.dispatch('raiseSuccess', `Product ${this.product._jv.id} created`);
            this.endAction();
          }
        }
      },

      async deleteProduct() {
        this.deleteDialogPending = true;

        try {
          let resp = await this.$store.dispatch('jv/delete', this.product);

          this.$store.dispatch('raiseSuccess', `Product ${this.product._jv.id} removed`);
          this.endAction();
        } finally {
          this.deleteDialogPending = false;
        }
      }
    }
  }
</script>

